import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Crown, Shield, User, Trash2, Star, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Header } from '../components/Header/Header';
import { useAuthStore } from '../stores/auth.store';
import { useAuth } from '../features/auth/contexts/AuthContext';
import {
  useOrgMembersQuery,
  useAddMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useSetDefaultOrgMutation,
} from '../hooks/useOrganizationsQuery';
import type { MembershipRole } from '../hooks/useOrganizationsApi';
import { roleBadgeClass } from '../lib/org-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';

/**
 * Role icon mapping for visual role indicators.
 */
const RoleIcon: React.FC<{ role: MembershipRole }> = ({ role }) => {
  switch (role) {
    case 'owner':
      return <Crown className="h-4 w-4 text-amber-500" />;
    case 'admin':
      return <Shield className="h-4 w-4 text-blue-500" />;
    default:
      return <User className="h-4 w-4 text-gray-500" />;
  }
};

/**
 * Organization Management page.
 * Shows the active organization's details, members list with role badges,
 * and admin controls for inviting/removing members and changing roles.
 */
export const OrgManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const { activeOrgId, organizations, defaultOrgId } = useAuthStore();

  const activeOrg = organizations.find((m) => m.orgId === activeOrgId);
  const userRole = activeOrg?.role;
  const canManageMembers = userRole === 'owner' || userRole === 'admin';

  const { data: members = [], isLoading: isLoadingMembers } = useOrgMembersQuery(
    activeOrgId || '',
    !!activeOrgId,
  );

  const addMember = useAddMemberMutation();
  const updateRole = useUpdateMemberRoleMutation();
  const removeMember = useRemoveMemberMutation();
  const setDefault = useSetDefaultOrgMutation();

  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteRole, setInviteRole] = useState<MembershipRole>('member');
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  /**
   * Handle adding a new member to the organization.
   */
  const handleAddMember = async () => {
    if (!activeOrgId || !inviteUserId.trim()) return;
    try {
      await addMember.mutateAsync({
        orgId: activeOrgId,
        data: { userId: inviteUserId.trim(), role: inviteRole },
      });
      toast.success(t('organizations.memberAdded', 'Member added successfully'));
      setInviteUserId('');
      setInviteRole('member');
      setIsInviteOpen(false);
    } catch {
      toast.error(t('organizations.addMemberError', 'Failed to add member'));
    }
  };

  /**
   * Handle changing a member's role.
   */
  const handleUpdateRole = async (userId: string, newRole: MembershipRole) => {
    if (!activeOrgId) return;
    try {
      await updateRole.mutateAsync({
        orgId: activeOrgId,
        userId,
        data: { role: newRole },
      });
      toast.success(t('organizations.roleUpdated', 'Role updated'));
    } catch {
      toast.error(t('organizations.updateRoleError', 'Failed to update role'));
    }
  };

  /**
   * Handle removing a member from the organization.
   */
  const handleRemoveMember = async (userId: string) => {
    if (!activeOrgId) return;
    try {
      await removeMember.mutateAsync({ orgId: activeOrgId, userId });
      toast.success(t('organizations.memberRemoved', 'Member removed'));

      // If the current user removed themselves, refresh orgs and redirect
      const currentUserId = useAuthStore.getState().user?.id;
      if (currentUserId === userId) {
        await useAuthStore.getState().fetchOrganizations();
        navigate('/dashboard');
      }
    } catch {
      toast.error(t('organizations.removeMemberError', 'Failed to remove member'));
    }
  };

  /**
   * Handle setting the active org as the default.
   */
  const handleSetDefault = async () => {
    if (!activeOrgId) return;
    try {
      await setDefault.mutateAsync(activeOrgId);
      // Sync Zustand store so defaultOrgId and isDefault flags are up to date
      await useAuthStore.getState().fetchOrganizations();
      toast.success(t('organizations.setDefaultSuccess', 'Default organization updated'));
    } catch {
      toast.error(t('organizations.setDefaultError', 'Failed to set default organization'));
    }
  };

  if (!activeOrg) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <Header onLogout={logout} />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground" data-testid="no-org-message">
            {t('organizations.noActiveOrg', 'No active organization selected.')}
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Header onLogout={logout} />
      <main className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          {/* Back Navigation */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('organizations.backToDashboard', 'Back to Dashboard')}
          </Button>

          {/* Organization Header */}
          <Card className="mb-6" data-testid="org-header-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="h-8 w-8 text-primary" />
                  <div>
                    <CardTitle className="text-xl">{activeOrg.organization.name}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Badge className={roleBadgeClass[activeOrg.role]}>{activeOrg.role}</Badge>
                      {activeOrg.isDefault && (
                        <Badge variant="outline" className="gap-1">
                          <Star className="h-3 w-3" />
                          {t('organizations.default', 'Default')}
                        </Badge>
                      )}
                    </CardDescription>
                  </div>
                </div>
                {!activeOrg.isDefault && activeOrgId !== defaultOrgId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSetDefault}
                    disabled={setDefault.isPending}
                    data-testid="set-default-btn"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    {t('organizations.setAsDefault', 'Set as Default')}
                  </Button>
                )}
              </div>
            </CardHeader>
          </Card>

          {/* Members Section */}
          <Card data-testid="members-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  <CardTitle className="text-lg">
                    {t('organizations.members', 'Members')} ({members.length})
                  </CardTitle>
                </div>
                {canManageMembers && (
                  <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="invite-member-btn">
                        {t('organizations.addMember', 'Add Member')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="invite-dialog">
                      <DialogHeader>
                        <DialogTitle>
                          {t('organizations.addMemberTitle', 'Add a New Member')}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label htmlFor="invite-user-id">
                            {t('organizations.userId', 'User ID')}
                          </Label>
                          <Input
                            id="invite-user-id"
                            value={inviteUserId}
                            onChange={(e) => setInviteUserId(e.target.value)}
                            placeholder={t('organizations.userIdPlaceholder', 'Enter user ID...')}
                            data-testid="invite-user-id-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="invite-role">{t('organizations.role', 'Role')}</Label>
                          <Select
                            value={inviteRole}
                            onValueChange={(val) => setInviteRole(val as MembershipRole)}
                          >
                            <SelectTrigger data-testid="invite-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={handleAddMember}
                          disabled={addMember.isPending || !inviteUserId.trim()}
                          className="w-full"
                          data-testid="confirm-invite-btn"
                        >
                          {addMember.isPending
                            ? t('organizations.adding', 'Adding...')
                            : t('organizations.addMember', 'Add Member')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="pt-4">
              {isLoadingMembers ? (
                <p className="text-sm text-muted-foreground" data-testid="loading-members">
                  {t('organizations.loadingMembers', 'Loading members...')}
                </p>
              ) : members.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="no-members">
                  {t('organizations.noMembers', 'No members found.')}
                </p>
              ) : (
                <div className="space-y-3" data-testid="members-list">
                  {members.map((member) => (
                    <div
                      key={member.userId}
                      className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`member-row-${member.userId}`}
                    >
                      <div className="flex items-center gap-3">
                        <RoleIcon role={member.role} />
                        <div>
                          <p className="text-sm font-medium">
                            {member.firstName || member.lastName
                              ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                              : member.email}
                          </p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canManageMembers && member.role !== 'owner' ? (
                          <>
                            <Select
                              value={member.role}
                              onValueChange={(val) =>
                                handleUpdateRole(member.userId, val as MembershipRole)
                              }
                            >
                              <SelectTrigger
                                className="h-8 w-28"
                                data-testid={`role-select-${member.userId}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleRemoveMember(member.userId)}
                              disabled={removeMember.isPending}
                              data-testid={`remove-member-${member.userId}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Badge className={roleBadgeClass[member.role]}>{member.role}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
